import Promise from 'bluebird';
import bootbox from 'bootbox';
import _ from 'lodash';
import moment from 'moment';

import { CONFIG } from '../../config';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { treatStoreTypeSaleMarketValue } from '../../models/StoreTypeSaleMarket';
import { setExportActionCss } from '../../utils';
import api from '../api';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import templates from '../core/templates';
import translate from '../core/tools/translate';
import { VehicleTypeEnum } from '../core/vehicleType/constants';
import environment from '../environment';
import { customScrollbar, isLayerEnabled } from '../shared';
import { selectMarker } from '../shared/map';
import { exportStore, storeExportType } from '../core/export/store';
import { feature } from 'topojson-client';

const LayerControls = (name) => require('../controls').get(name);
const LayerInfo = (name) => require('./index').get(name);
const currentLayer = (name) => LayerControls(name).getLayer();
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const _getContainer = options => infoBoard.createContainer(options);

const _getImage = (container, layer, feature) =>
    new Promise((resolve, reject) => {
        const id = feature.properties.IdOriginal;

        const images = {
            hasPhoto: true,
            original: `${CONFIG.STORE_IMAGES_HOST}/${id}.jpg`,
            thumbnail: `${CONFIG.STORE_THUMBS_HOST}/${id}.jpg`
        };

        const thumbnail = new Image();

        thumbnail.onload = () => resolve(images);

        thumbnail.onerror = () => {
            const latlng = {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0]
            };

            resolve({
                hasPhoto: false,
                original: '#',
                thumbnail: `https://maps.googleapis.com/maps/api/streetview?size=${worldMap.getThumbImageSize()}&location=${latlng.lat},${latlng.lng}&sensor=false&key=${CONFIG.GOOGLE_MAPS_API_KEY}`
            });
        };

        thumbnail.src = images.thumbnail;
    });

const _getSalesBySegment = (id, context) =>
    new Promise((resolve, reject) => {
        if (context === 'storesMotocycle' || context === 'storesConsortium') {
            resolve(null);
            return;
        }

        const success = res => resolve(res);
        const error = () => resolve();

        api.store.get.sales.bySegment(id).then(success, error);
    });

const _getSales = (id, context) =>
    new Promise((resolve, reject) => {
        if (!isAuthorized(FeaturePermission.INFO_STORE.showSaleChartAnnual) || !isAuthorized(FeaturePermission.SYSTEM.showDataRetail) || context === 'storesMotocycle' || context === 'storesConsortium') {
            resolve(null);
            return;
        }

        const mountPeriod = (months) => {
            const nums = months.map(m => Number(m) || 0);
            if (nums.length === 0 || nums.every(n => n === 0)) {
                return "-";
            };

            const _firstMonth = (_.minBy(months) || {});
            const firstCycle = `${moment(_firstMonth, 'MM').format('MMM')}`;

            const _lastMonth = (_.maxBy(months) || {});
            const lastCycle = `${moment(_lastMonth, 'MM').format('MMM')}`;

            return firstCycle === lastCycle
                ? firstCycle
                : `${firstCycle} - ${lastCycle}`;
        };

        const success = res => {
            if (!res || !_.size(res.sales)) { return resolve(); }

            const sales = {
                chart: res.chart,
                grid: res.grid,
                variation: res.variation,
                prorated: false
            };

            sales.past = {
                year: res.previousYear.ano,
                total: res.previousYear.media
            };

            sales.current = {
                year: res.lastYear.ano,
                total: res.lastYear.media
            };

            sales.firstPeriod = mountPeriod(res.previousYear.emplacamentosMes.map(x => x.mes));
            sales.lastPeriod = mountPeriod(res.lastYear.emplacamentosMes.map(x => x.mes));

            resolve(sales);
        };

        const error = () => resolve();

        api.store.get.sales.history(id).then(success, error);
    });

const _getInfo = (feature, resultType) => api.store.get(feature.properties.Id, resultType);

const _getInfoUnpprovedStores = (feature) => api.store.get.unapprovedStores(feature.properties.Id);

const _getInfoStorePlanned = (feature) => api.store.get.storeplannedinfo(feature.properties.Id);

class StoresInfo {
    static getInstance() {
        if (!StoresInfo._instance) {
            StoresInfo._instance = new StoresInfo();
        }

        return StoresInfo._instance;
    }

    init() {
    }

    _getFeatureLayer(resultType, feature) {
        return currentLayer(resultType).getLayer(feature.properties.Id);
    }

    _triggerAction(value, layer, feature, resultType, context) {
        api.general.log.action(environment._getContextEnum(resultType), `Info - ${value}`, JSON.stringify(feature.properties));
        switch (value) {
            case 'locate':
                return worldMap.setView(layer.getLatLng(), 16);
            case 'recalculate':
                return this._recalculateArea(feature, layer, resultType);
            // case 'reporting':
            //     return LayerInfo('CreatePoint').reporting(currentLayer(resultType), feature, context);
            // case 'edit':
            //     return LayerInfo('CreatePoint').edit(currentLayer(resultType), feature, context);
            case 'remove':
                return bootbox.dialog({
                    title: '<i class="fa fa-warning"></i> Aviso',
                    message: 'Você tem certeza de que deseja remover o ponto selecionado? Esta ação não poderá ser desfeita.',
                    backdrop: false,
                    buttons: {
                        main: {
                            label: '<i class="fa fa-times"></i> Cancelar',
                            className: 'btn-default'
                        },
                        danger: {
                            label: '<i class="fa fa-trash-o"></i> Remover',
                            className: 'btn-danger',
                            callback() {
                                return api.points.remove(feature.properties.Id);
                            }
                        }
                    }
                });
        }
    }

    _showInformation(feature, item, layer, context, resultType) {
        const intL = new Intl.DateTimeFormat(translate.language());
        api.general.log.action(environment._getContextEnum(resultType), 'Info - Ficha da Loja', JSON.stringify(feature.properties));
        const queue = [];
        queue.push(_getImage(item.image, layer, feature));

        if (context !== "unapprovedStores" && context !== "storePlanned") {
            queue.push(_getInfo(feature, resultType));
            queue.push(_getSales(feature.properties.Id, context));
            queue.push(_getSalesBySegment(feature.properties.Id, context));
            Promise.all(queue).then(response => {
                const data = response[1];
                data.image = response[0];
                data.sales = response[2];
                if (data.sales) { data.sales.prorated = data.information.IndicadorVendaRateada; }
                data.salesBySegments = response[3];
                data.name = feature.properties.Nome.toCapitalCase();
                data.id = feature.properties.IdOriginal;
                data.storeId = feature.properties.Id;
                data.report = false;
                data.information.tipo = translate(feature.properties.Tipo);
                data.information.dealerNumber = feature.properties.IdLojaCliente;
                data.information.dataAberturaReceitaFederal = data.information.dataAberturaReceitaFederal
                    ? intL.format(new Date(data.information.dataAberturaReceitaFederal))
                    : null;
                data.information.dataFechamento = data.information.dataFechamento
                    ? intL.format(new Date(data.information.dataFechamento))
                    : null;
                data.information.dataInauguracao = data.information.dataInauguracao
                    ? intL.format(new Date(data.information.dataInauguracao))
                    : null;

                if (data.information.tipoVendaMercado) {
                    data.information.storeTypeSaleMarket = treatStoreTypeSaleMarketValue(data.information.tipoVendaMercado);
                }

                const vehicleType = environment.get('CONFIG.USERVEHICLETYPE');
                const pathImage = vehicleType === VehicleTypeEnum.Moto || context === 'storesMotocycle' ? '/motos/' : '/';
                data.logo = `${environment.get('IMAGES_HOST')}${pathImage}logo_${String(item.feature.properties['Bandeira']).toUnderscoreCase()}.png`;

                if (context === 'storesConsortium' && item.feature.properties['Tipo']) {
                    data.logo = `${environment.get('IMAGES_HOST')}${pathImage}logo_${String(item.feature.properties['Tipo']).toUnderscoreCase()}.png`;
                }
                data.style = 'style=width: 42px; height: 42px;';

                LayerInfo('Resume').show(data, {
                    context,
                    resultType,
                    title: translate('Common.StoreInfo'),
                    callback: value => this._triggerAction(value, layer, feature, resultType, context),
                    callbackBack: () => {
                        infoBoard.showInfo(resultType, feature.properties.Id);
                        worldMap.getCurrent().fireEvent('click');
                    },
                    visible: true,
                    latlng: layer.getLatLng()
                });
            });
        }

        if (context === "unapprovedStores" || context === "storePlanned") {
            if (context === "unapprovedStores") {
                queue.push(_getInfoUnpprovedStores(feature));
            }

            if (context === "storePlanned") {
                queue.push(_getInfoStorePlanned(feature));
            }

            Promise.all(queue).then(response => {
                const data = response[1];
                data.image = response[0];
                data.name = feature.properties.Nome.toCapitalCase();
                data.id = feature.properties.Id;
                data.report = false;

                const vehicleType = environment.get('CONFIG.USERVEHICLETYPE');
                const pathImage = vehicleType === VehicleTypeEnum.Moto ? '/motos/' : '/';
                data.logo = context === "unapprovedStores" ?
                `${environment.get('IMAGES_HOST')}${pathImage}logo_atention.png` :
                `${environment.get('IMAGES_HOST')}${pathImage}logo_lojas_previstas.png`;
                data.style = 'style=width: 42px; height: 42px;';
                data.information.dealerNumber = feature.properties.IdLojaCliente;

                LayerInfo('Resume').show(data, {
                    context,
                    resultType,
                    title: translate('Common.StoreInfo'),
                    callback: value => this._triggerAction(value, layer, feature, resultType, context),
                    callbackBack: () => {
                        infoBoard.showInfo(resultType, feature.properties.Id);
                        worldMap.getCurrent().fireEvent('click');
                    },
                    visible: true,
                    latlng: layer.getLatLng()
                });
            });
        }
    }

    _addAreaInfluence(feature, layer, context, resultType) {
        if (isAuthorized(FeaturePermission.MAP_STORE.influenceArea)) {
            this._recalculateArea(feature, layer, resultType);
        }
    }

    _recalculateArea(feature, layer, resultType) {
        LayerInfo('Summary').recalculate({
            id: feature.properties.Id,
            latlng: layer.getLatLng(),
            value: feature.properties.RaioServiceAreaMt,
            layer: currentLayer(resultType),
            controls: LayerControls(resultType).getControls(),
            update: true,
            feature
        });
    }

    _updateVoronoi(feature, layer) {
        if (layer && worldMap.hasLayer(layer)) {
            return LayerControls('Voronoi').add([feature]);
        } else {
            return LayerControls('Voronoi').remove([feature.properties.Id]);
        }
    }

    create(features, resultType) {
        if (infoBoard.locked) {
            return;
        }

        const contexts = {
            'Dealerships': {
                context: 'stores',
                contextLabel: `${translate('Common.Dealerships')}`,
            },
            'StoreInactive': {
                context: 'storeInactive',
                contextLabel: translate('Common.StoreInactive'),
            },
            'Competitors': {
                context: 'competitors',
                contextLabel: translate('Common.Competitors'),
            },
            'StoresMotocycle': {
                context: 'storesMotocycle',
                contextLabel: `${translate('Common.StoresMotocycle')}`,
            },
            'StoresConsortium': {
                context: 'storesConsortium',
                contextLabel: `${translate('Common.StoresConsortium')}`,
            },
            'UnapprovedStores': {
                context: 'unapprovedStores',
                contextLabel: translate('Common.UnapprovedStores'),
            },
            'StorePlanned': {
                context: 'storePlanned',
                contextLabel: 'Lojas Planejadas',
            },
        };

        const { context, contextLabel } = contexts[resultType];

        const container = _getContainer({
            id: resultType.toLowerCase(),
            name: contextLabel
        });

        if (infoBoard.dataUnavailable({
            features,
            container,
            isEnabled: isLayerEnabled(resultType)
        })) {
            return;
        }

        const $template = $(templates('infoboard-content', {
            id: `${resultType.toLowerCase()}-collapse`,
            actions: isAuthorized(FeaturePermission.INFO_STORE.exportDealerEvaluation)
        }));
        const content = $template.find('.panel-group');
        const items = _.reduce(features, (acc, feature) => {
            let layer = this._getFeatureLayer(resultType, feature);

            const options = {
                feature,
                name: resultType.toLowerCase(),
                originalName: resultType,
                container,
                controls: {
                    info: isAuthorized(FeaturePermission.INFO_STORE.enable),
                    createInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && !feature.properties.PossuiServiceArea && resultType !== "UnapprovedStores",
                    removeInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && feature.properties.PossuiServiceArea,
                    updateInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && feature.properties.PossuiServiceArea,
                    hideStore: true
                }
            };

            const item = infoBoard.createItem(options);
            if (!item) {
                return;
            }

            if (item.btn.info) {
                item.btn.info.on('click', () => {
                    this._showInformation(feature, item, layer, context, resultType);
                    selectMarker(layer, feature, resultType, context);
                });
            }

            if (item.btn.createInfluenceArea) {
                item.btn.createInfluenceArea.on('click', () =>
                    this._addAreaInfluence(feature, layer, context, resultType)
                );
            }

            if (item.btn.updateInfluenceArea) {
                item.btn.updateInfluenceArea.on('click', () =>
                    this._addAreaInfluence(feature, layer, context, resultType)
                );
            }

            if (item.btn.removeInfluenceArea) {
                item.btn.removeInfluenceArea.on('click', () => {
                    LayerInfo('Summary').deleteServiceArea({
                        id: feature.properties.Id,
                        latlng: layer.getLatLng(),
                        value: feature.properties.RaioServiceAreaMt,
                        layer: currentLayer(resultType),
                        controls: LayerControls(resultType).getControls(),
                        feature
                    });
                });
            }

            if (item.btn.hideStore) {
                // toggle icon if the current feature was target as hidden
                if (currentLayer(resultType).isHidden(feature.properties.Id)) {
                    $(item.btn.hideStore).parent().find('i').toggleClass('fa-eye-slash');
                    $(item.btn.hideStore).parent().find('a').text(translate('Action.ShowStore'));
                    item.title.toggleClass('disabled');
                    this._updateVoronoi(feature);
                }

                if (!currentLayer(resultType).isHidden(feature.properties.Id)) {
                    $(item.btn.hide).parent().find('i').toggleClass('fa-eye');
                    $(item.btn.hide).parent().find('a').text(translate('Action.HideStore'));
                }

                item.btn.hideStore.on('click', () => {
                    LayerControls(resultType).toggleVisibility(feature.properties.Id, layer);
                    if (!layer) {
                        layer = this._getFeatureLayer(resultType, feature);
                    }

                    return this._updateVoronoi(feature, layer);
                });
            }

            // item.title.on('mouseover', () => LayerControls(resultType).highlight(layer));

            // item.title.on 'click', (e) ->
            //  currentLayer(resultType).panTo(layer.getLatLng())

            if (LayerInfo('Compare').getStatus() && LayerInfo('Compare').check(feature.properties.Id)) {
                acc = [item.layout, ...acc];
            } else {
                acc.push(item.layout);
            }

            return acc;
        }, []);

        // give the DOM a break
        $(content).append(items);
        $(container).detach().html($template).appendTo('#infoboard-content');

        const actions = infoBoard.createActions({
            export: {
                value: isAuthorized(FeaturePermission.INFO_STORE.export),
                label: translate('Action.ExportStores')
            },
            compare: {
                value: false // isAuthorized("#{context}-comparison-view")
            }
        }, context);

        actions.layout.appendTo(container);

        if (actions.compare) {
            $(actions.compare).on('click', () => {
                if (LayerInfo('Compare').getStatus()) {
                    return;
                }

                infoBoard.enableCompare(container);
                if (isLayerEnabled('PointOfInterest')) {
                    infoBoard.enableCompare('#points-tab');
                }

                LayerInfo('Compare').create();
            });

            // compare points
            if (LayerInfo('Compare').getStatus()) {
                infoBoard.enableCompare(container, resultType);
            }
        }

        if (actions.export) {
            setExportActionCss();
            $(actions.export).on('click', e => {
                e.stopPropagation();
                return exportStore(storeExportType[resultType]);
            });
        }

        customScrollbar(content);
    }

    remove() {
        LayerInfo('Resume').remove();
    }
}

export default StoresInfo;



// WEBPACK FOOTER //
// ./src/scripts/info/StoresInfo.js